import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import CardChartTotal from '@/components/ui/card-wrapper/CardChartTotal';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import MonthDropdown from '@/components/ui/date-picker/MonthDropdown';
import { fCurrencyUSD, fCurrencyUSDExtended, fNumber } from '@/utils/formatNumber';
import { shortEnglishHumanizer } from '@/utils/formatTime';
import dayjs from 'dayjs';
import { Clock, DollarSign, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import TasksMetrics from './metrics/tasks';
import CostMetrics from './metrics/cost';
import {
  useGetServerlessMetricsDailyCost,
  useGetServerlessMetricsDailyTasks,
  useGetServerlessMetricsTotalConsumption,
} from '@/service/serverless-gpu/hook';
import { useParams } from 'react-router-dom';

const ServerlessActivity = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [month, setMonth] = useState<string>(dayjs().format('YYYY-MM'));
  const handleSelect = (selectedMonth: string) => {
    setMonth(selectedMonth);
  };

  const { data: dataTotalConsumption, isLoading: isLoadingTotalConsumption } =
    useGetServerlessMetricsTotalConsumption({
      project_id: projectId!,
      start_date: dayjs(month).startOf('month').format(),
      end_date: dayjs(month).endOf('month').format(),
    });

  const { data: dataDailyCost, isLoading: isLoadingDailyCost } =
    useGetServerlessMetricsDailyCost({
      project_id: projectId!,
      start_date: dayjs(month).startOf('month').format(),
      end_date: dayjs(month).endOf('month').format(),
    });

  const { data: dataDailyTasks, isLoading: isLoadingDailyTasks } =
    useGetServerlessMetricsDailyTasks({
      project_id: projectId!,
      start_date: dayjs(month).startOf('month').format(),
      end_date: dayjs(month).endOf('month').format(),
    });

  const data = {
    totalTasks: dataTotalConsumption?.total_tasks || 0,
    totalCostUSD: dataTotalConsumption?.total_cost || 0,
    totalUsage: (dataTotalConsumption?.total_usage || 0) * 1000,
    costBreakdown: {
      onDemandCost: dataTotalConsumption?.cost_breakdown.on_demand || 0,
      spotCost: dataTotalConsumption?.cost_breakdown.spot || 0,
      storageCost: dataTotalConsumption?.cost_breakdown.storage || 0,
    },
    usageBreakdown: {
      onDemandUsage:
        (dataTotalConsumption?.usage_breakdown.on_demand || 0) * 1000,
      spotUsage: (dataTotalConsumption?.usage_breakdown.spot || 0) * 1000,
    },
    metrics: {
      tasks:
        Object.values(
          (dataDailyTasks?.data || []).reduce(
            (acc, item) => {
              const dayKey = dayjs(item.date).startOf('day').valueOf();
              if (!acc[dayKey]) {
                acc[dayKey] = {
                  date: dayKey,
                  total: 0,
                  manual: 0,
                  function: 0,
                  server: 0,
                  spot: 0,
                };
              }
              acc[dayKey].total += item.total_tasks;
              acc[dayKey].manual += item.manual;
              acc[dayKey].function += item.function;
              acc[dayKey].server += item.server;
              acc[dayKey].spot += item.spot;
              return acc;
            },
            {} as Record<number, any>
          )
        ) || [],
      cost:
        Object.values(
          (dataDailyCost?.data || []).reduce(
            (acc, item) => {
              const dayKey = dayjs(item.date).startOf('day').valueOf();
              if (!acc[dayKey]) {
                acc[dayKey] = {
                  date: dayKey,
                  total: 0,
                  storage: 0,
                  onDemand: 0,
                  spot: 0,
                };
              }
              acc[dayKey].total += item.total_cost;
              acc[dayKey].storage += item.storage;
              acc[dayKey].onDemand += item.on_demand;
              acc[dayKey].spot += item.spot;
              return acc;
            },
            {} as Record<number, any>
          )
        ) || [],
    },
  };

  return (
    <div className="container mt-8">
      <MonthDropdown onSelect={handleSelect} defaultMonth={month} />
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card className="">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="">
              <div className="text-2xl font-bold">
                {fCurrencyUSDExtended(data.totalCostUSD)}
              </div>
              <div className="mt-2 flex flex-col gap-1 text-sm">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-[hsl(var(--chart-1))]" />
                  <span className="ml-1 text-xs text-muted-foreground">
                    On-Demand: {fCurrencyUSDExtended(data.costBreakdown.onDemandCost)}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-[hsl(var(--chart-4))]" />
                  <span className="ml-1 text-xs text-muted-foreground">
                    Spot: {fCurrencyUSDExtended(data.costBreakdown.spotCost)}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-[hsl(var(--chart-3))]" />
                  <span className="ml-1 text-xs text-muted-foreground">
                    Storage: {fCurrencyUSDExtended(data.costBreakdown.storageCost)}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-2">
              <ChartContainer config={{}} className="max-h-[30px] w-full">
                <BarChart
                  accessibilityLayer
                  data={[
                    {
                      name: 'Cost Breakdown',
                      'On-Demand': data.costBreakdown.onDemandCost,
                      Spot: data.costBreakdown.spotCost,
                      Storage: data.costBreakdown.storageCost,
                    },
                  ]}
                  layout="vertical"
                  stackOffset="expand"
                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                >
                  <XAxis type="number" hide />
                  <YAxis type="category" hide dataKey="name" />
                  <ChartTooltip
                    content={<ChartTooltipContent className="w-[200px]" />}
                  />
                  <Bar
                    dataKey="On-Demand"
                    stackId="a"
                    fill="hsl(var(--chart-1))"
                    radius={[4, 0, 0, 4]}
                  />
                  <Bar dataKey="Spot" stackId="a" fill="hsl(var(--chart-4))" />
                  <Bar
                    dataKey="Storage"
                    stackId="a"
                    fill="hsl(var(--chart-3))"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>
        <Card className="flex min-h-0 flex-col">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <Clock className="h-4 w-4" />
          </CardHeader>
          <CardContent className="flex min-h-0 flex-grow flex-col justify-between">
            <div className="text-2xl font-bold">
              {shortEnglishHumanizer(data.totalUsage, {
                units: ['h', 'm', 's'],
                delimiter: ' ',
                maxDecimalPoints: 2,
              })}
            </div>
            <div className="">
              <div className="mt-2 flex flex-col gap-1 text-sm">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-[hsl(var(--chart-1))]" />
                  <span className="ml-1 text-xs text-muted-foreground">
                    On-Demand:{' '}
                    {shortEnglishHumanizer(data.usageBreakdown.onDemandUsage, {
                      units: ['h', 'm', 's'],
                      delimiter: ' ',
                      maxDecimalPoints: 2,
                    })}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-[hsl(var(--chart-4))]" />
                  <span className="ml-1 text-xs text-muted-foreground">
                    Spot:{' '}
                    {shortEnglishHumanizer(data.usageBreakdown.spotUsage, {
                      units: ['h', 'm', 's'],
                      delimiter: ' ',
                      maxDecimalPoints: 2,
                    })}
                  </span>
                </div>
              </div>

              <div className="mt-2">
                <ChartContainer config={{}} className="max-h-[30px] w-full">
                  <BarChart
                    accessibilityLayer
                    data={[
                      {
                        name: 'Usage Breakdown',
                        'On-Demand': data.usageBreakdown.onDemandUsage,
                        Spot: data.usageBreakdown.spotUsage,
                      },
                    ]}
                    layout="vertical"
                    stackOffset="expand"
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis type="category" hide dataKey="name" />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          className="w-[200px]"
                          valueFormatter={(value) => {
                            return shortEnglishHumanizer(Number(value), {
                              units: ['h', 'm', 's'],
                              delimiter: ' ',
                              maxDecimalPoints: 2,
                            });
                          }}
                        />
                      }
                    />
                    <Bar
                      dataKey="On-Demand"
                      stackId="a"
                      fill="hsl(var(--chart-1))"
                      radius={[4, 0, 0, 4]}
                    />
                    <Bar
                      dataKey="Spot"
                      stackId="a"
                      fill="hsl(var(--chart-4))"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <CardChartTotal title="Cost" description="Total Cost (USD)">
          <CostMetrics data={data.metrics.cost || []} />
        </CardChartTotal>
        <CardChartTotal
          title="Tasks"
          description="Total Tasks by trigger type (Number of tasks)"
          total={data.totalTasks}
        >
          <TasksMetrics data={data.metrics.tasks || []} />
        </CardChartTotal>
      </div>
    </div>
  );
};

export default ServerlessActivity;
